import { z } from "zod";
import { fileUrl } from "./common";
import {
  TESTIMONIAL_ROLES,
  TESTIMONIAL_PLACEMENTS,
  type TestimonialRole,
  type TestimonialPlacement,
} from "@/types/testimonials";

const role = z.enum(TESTIMONIAL_ROLES as unknown as [TestimonialRole, ...TestimonialRole[]]);
const placement = z.enum(
  TESTIMONIAL_PLACEMENTS as unknown as [TestimonialPlacement, ...TestimonialPlacement[]],
);

/**
 * Card quote limit. Migrated home rows go up to 988 characters, so the cap sits
 * above that: re-saving an untouched row must never fail on its length.
 */
export const TESTIMONIAL_QUOTE_MAX = 1200;
export const TESTIMONIAL_FULL_QUOTE_MAX = 8000;

const quote = z.string().trim().min(1).max(TESTIMONIAL_QUOTE_MAX);
// Seeded rows carry "", so empty stays valid; anything else must be an uploaded file.
const avatarUrl = fileUrl.or(z.literal(""));

/** Long quote behind the "read full review" dialog — several paragraphs, not a card blurb. */
const fullQuote = z.string().max(TESTIMONIAL_FULL_QUOTE_MAX);

export const createTestimonialSchema = z.object({
  placement: placement.optional().default("home"),
  name: z.string().min(1).max(120),
  role,
  roleBs: z.string().max(160).optional().default(""),
  roleEn: z.string().max(160).optional().default(""),
  quoteBs: quote,
  quoteEn: quote,
  fullQuoteBs: fullQuote.optional().default(""),
  fullQuoteEn: fullQuote.optional().default(""),
  // Not rendered by either placement today; kept so curated rows can carry one.
  avatarUrl: avatarUrl.optional().default(""),
  rating: z.number().int().min(1).max(5).optional().default(5),
  isActive: z.boolean().optional().default(true),
});

// `placement` is deliberately absent: sortOrder is scoped per placement, so
// moving a row between placements would land it on a taken slot. Delete and
// recreate instead.
export const updateTestimonialSchema = z
  .object({
    name: z.string().min(1).max(120),
    role,
    roleBs: z.string().max(160),
    roleEn: z.string().max(160),
    quoteBs: quote,
    quoteEn: quote,
    fullQuoteBs: fullQuote,
    fullQuoteEn: fullQuote,
    avatarUrl,
    rating: z.number().int().min(1).max(5),
    isActive: z.boolean(),
    direction: z.enum(["up", "down"]),
  })
  .partial();
